import { SVGAssets } from '@business/assets/vectors';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { routePaths } from '@utils/constants/routePaths';
import { useAddUserDetailsMutation } from '@ports/internal-services/userDetails/userDetailsSlice';
import { useState, FC } from 'react';
import { setCommonState } from '@ports/common/commonStateSlice';
import { useDispatch } from 'react-redux';
import { setAlertDetails } from '@ports/common/AlertStateSlice';
import { ConfirmationBox } from '../ConfirmationBox';
import { UploadPhotoModal } from './UploadPhotoModal';

interface UploadOptionsProps {
  showUploadModal: boolean;
  setShowUploadModal: (value: boolean) => void;
  userId: string;
  onUploadSuccess?: () => void; // Add callback prop
}

export const UploadOptions: FC<UploadOptionsProps> = ({
  showUploadModal,
  setShowUploadModal,
  userId,
  onUploadSuccess
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isOpenConfirmation, setIsOpenConfirmation] = useState<boolean>(false);

  const [UpdateUserDetails] = useAddUserDetailsMutation();

  const handleClose = () => {
    setIsOpenConfirmation(false);
  };

  const removeHandler = () => {
    const dataObj = {
      createUserDetails: {
        profileImage: ''
      }
    };
    UpdateUserDetails(dataObj)
      .then((res: any) => {
        if (res?.data) {
          if (onUploadSuccess) {
            onUploadSuccess();
          }
          dispatch(
            setAlertDetails({
              alert: {
                type: 'success',
                message: t('applySuccess')
              },
              scope: 'profile'
            })
          );
          setIsOpenConfirmation(false);
          navigate(routePaths.MyProfile);
        }
      })
      .catch(err => {
        dispatch(
          setAlertDetails({
            alert: {
              type: 'error',
              message: err.message
            },
            scope: 'profile'
          })
        );
      });
  };

  const handleUploadSuccess = async () => {
    // Call the refetch callback after successful upload
    if (onUploadSuccess) {
      await onUploadSuccess();
    }
    setShowUploadModal(false);
  };

  return (
    <>
      <div className="absolute top-[54%] left-[133%] -translate-x-1/2 bg-white shadow-lg rounded-lg py-2 w-[10rem] z-10">
        <div className="upload-option" onClick={() => setShowUploadModal(true)}>
          <SVGAssets.UploadIcon className="w-4 h-4 text-[#7F9D48]" />
          <span>{t('uploadPhoto')}</span>
        </div>
        <div
          className="upload-option"
          onClick={() => {
            dispatch(setCommonState({ isFromPhoto: false }));
            navigate(routePaths.Camera);
          }}>
          <SVGAssets.TakePicIcon className="w-4 h-4 text-[#7F9D48]" />
          <span>{t('takePicture')}</span>
        </div>
        <div
          className="upload-option"
          onClick={() => {
            setIsOpenConfirmation(true);
          }}>
          <SVGAssets.RemoveIcon className="w-4 h-4" />
          <span>{t('removePhoto')}</span>
        </div>
      </div>
      {isOpenConfirmation && (
        <ConfirmationBox
          isOpen={isOpenConfirmation}
          onCancel={handleClose}
          onClose={handleClose}
          onConfirm={removeHandler}
          headerTitle=""
          title={t('removePhoto')}
          description={t('removeProfilePhotoConfirmation')}
          icon={<SVGAssets.PaymentFailedIcon className="w-28 h-28" />}
          confirmText={t('continue')}
          cancelText={t('cancel')}
          className="text-center"
        />
      )}
      {showUploadModal && (
        <UploadPhotoModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal?.(false)}
          userId={userId}
          onUploadSuccess={handleUploadSuccess}
        />
      )}
    </>
  );
};
